import api from './api';

export interface Appointment {
  id?: number;
  work_order_id?: number;
  // SP-1 Task 5: linked work order's current status (LEFT JOIN work_orders in
  // findAllByShop/findByDateRangeAndShop). Null when there is no linked WO.
  work_order_status?: string | null;
  estimate_id?: number;
  technician_id: number;
  customer_id: number;
  vehicle_id: number;
  appointment_type: 'diagnosis' | 'repair' | 'inspection' | 'pickup' | 'delivery';
  title: string;
  description?: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  travel_time_minutes: number;
  buffer_time_minutes: number;
  location_address?: string;
  location_lat?: number;
  location_lng?: number;
  status: 'scheduled' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled' | 'no_show';
  manual_override: boolean;
  override_reason?: string;
  override_by_user_id?: number;
  has_conflict: boolean;
  conflict_acknowledged: boolean;
  waiting_on_parts: boolean;
  parts_expected_date?: string;
  customer_notified: boolean;
  customer_confirmed: boolean;
  confirmation_token?: string;
  reminder_sent: boolean;
  notes?: string;
  internal_notes?: string;
  created_by_user_id?: number;
  cancelled_at?: string;
  cancelled_by_user_id?: number;
  cancellation_reason?: string;
  completed_at?: string;
  created_at?: string;
  updated_at?: string;
  technician_name?: string;
  technician?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  technicians?: Array<{
    id?: number;
    appointment_id?: number;
    technician_id: number;
    is_primary: boolean;
    assigned_at?: string;
    assigned_by_user_id?: number;
    notes?: string;
    technician?: {
      id: number;
      first_name: string;
      last_name: string;
      email: string;
      role: string;
    };
  }>;
  customer?: any;
  vehicle?: any;
  parts?: AppointmentPart[];
  conflicts?: AppointmentConflict[];
}

export interface AppointmentPart {
  id?: number;
  appointment_id: number;
  work_order_line_part_id?: number;
  part_name: string;
  part_number?: string;
  quantity: number;
  status: 'needed' | 'ordered' | 'in_transit' | 'received' | 'installed';
  expected_delivery_date?: string;
  actual_delivery_date?: string;
  tracking_number?: string;
  supplier?: string;
  delay_notified: boolean;
  arrival_notified: boolean;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface AppointmentConflict {
  id?: number;
  appointment_id: number;
  conflict_type: 'double_booking' | 'tech_unavailable' | 'parts_not_ready' | 'overlapping_travel' | 'exceeds_capacity';
  conflict_severity: 'low' | 'medium' | 'high' | 'critical';
  conflicting_appointment_id?: number;
  description: string;
  resolution_status: 'unresolved' | 'acknowledged' | 'resolved' | 'ignored';
  resolution_action?: string;
  resolved_by_user_id?: number;
  resolved_at?: string;
  created_at?: string;
  conflicting_appointment?: Appointment;
}

export interface AvailableTimeSlot {
  technician_id: number;
  technician_name: string;
  start_time: string;
  end_time: string;
  available: boolean;
  reason?: string;
  conflicts?: string[];
  travel_time_minutes?: number;
  recommended?: boolean;
}

export interface ConflictCheckResult {
  has_conflicts: boolean;
  conflicts: AppointmentConflict[];
  can_override: boolean;
  warnings: string[];
}

export const appointmentApi = {
  // Appointments
  getAll: async (params?: {
    technician_id?: number;
    start_date?: string;
    end_date?: string;
    status?: string;
    work_order_id?: number;
    estimate_id?: number;
  }): Promise<Appointment[]> => {
    const response = await api.get(`/appointments`, {
      params
    });
    return response.data;
  },

  getById: async (id: number): Promise<Appointment> => {
    const response = await api.get(`/appointments/${id}`);
    return response.data;
  },

  create: async (appointment: Omit<Appointment, 'id' | 'created_at' | 'updated_at' | 'technician' | 'customer' | 'vehicle' | 'parts' | 'conflicts'>): Promise<Appointment> => {
    const response = await api.post(`/appointments`, appointment);
    return response.data;
  },

  update: async (id: number, appointment: Partial<Appointment>): Promise<Appointment> => {
    const response = await api.put(`/appointments/${id}`, appointment);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/appointments/${id}`);
  },

  cancel: async (id: number, cancelled_by_user_id: number, cancellation_reason?: string, notify_customer: boolean = true, reschedule_link?: string): Promise<any> => {
    const response = await api.post(`/appointments/${id}/cancel`, {
      cancelled_by_user_id,
      cancellation_reason,
      notify_customer,
      reschedule_link
    });
    return response.data;
  },

  complete: async (id: number): Promise<Appointment> => {
    const response = await api.post(`/appointments/${id}/complete`, {});
    return response.data;
  },

  // Availability
  checkAvailability: async (params: {
    technician_id?: number;
    start_date: string;
    end_date: string;
    duration_minutes: number;
    appointment_type: 'diagnosis' | 'repair' | 'inspection';
    customer_id?: number;
    location_address?: string;
  }): Promise<AvailableTimeSlot[]> => {
    const response = await api.get(`/appointments/availability/check`, {
      params
    });
    return response.data;
  },

  // Conflicts
  checkConflicts: async (data: {
    technician_id: number;
    start_time: string;
    end_time: string;
    appointment_id?: number;
  }): Promise<ConflictCheckResult> => {
    const response = await api.post(`/appointments/conflicts/check`, data);
    return response.data;
  },

  acknowledgeConflicts: async (id: number, user_id: number, reason: string): Promise<Appointment> => {
    const response = await api.post(`/appointments/${id}/conflicts/acknowledge`, {
      user_id,
      reason
    });
    return response.data;
  },

  // Parts
  addPart: async (appointmentId: number, part: Omit<AppointmentPart, 'id' | 'appointment_id' | 'created_at' | 'updated_at'>): Promise<AppointmentPart> => {
    const response = await api.post(`/appointments/${appointmentId}/parts`, part);
    return response.data;
  },

  updatePart: async (appointmentId: number, partId: number, part: Partial<AppointmentPart>): Promise<AppointmentPart> => {
    const response = await api.put(`/appointments/${appointmentId}/parts/${partId}`, part);
    return response.data;
  },

  deletePart: async (appointmentId: number, partId: number): Promise<void> => {
    await api.delete(`/appointments/${appointmentId}/parts/${partId}`);
  },

  // Appointment Editing
  reschedule: async (id: number, data: {
    new_start_time: string;
    new_end_time?: string;
    duration_minutes?: number;
    reason?: string;
    notify_customer?: boolean;
    changed_by_user_id: number;
    manual_override?: boolean;
    override_reason?: string;
  }): Promise<{ success: boolean; appointment: Appointment; message: string }> => {
    const response = await api.post(`/appointments/${id}/reschedule`, data);
    return response.data;
  },

  changeTechnician: async (id: number, data: {
    new_technician_id: number;
    reason?: string;
    notify_customer?: boolean;
    changed_by_user_id: number;
    manual_override?: boolean;
    override_reason?: string;
  }): Promise<{ success: boolean; appointment: Appointment; message: string }> => {
    const response = await api.post(`/appointments/${id}/change-technician`, data);
    return response.data;
  },

  getRescheduleOptions: async (id: number, params?: {
    start_date?: string;
    end_date?: string;
    technician_id?: number;
    max_results?: number;
  }): Promise<{
    appointment: any;
    suggested_slots: AvailableTimeSlot[];
    total_suggestions: number;
  }> => {
    const response = await api.get(`/appointments/${id}/reschedule-options`, {
      params
    });
    return response.data;
  },

  getHistory: async (id: number): Promise<{
    appointment_id: number;
    history: Array<{
      id: number;
      change_type: string;
      field_changed: string;
      old_value: string;
      new_value: string;
      changed_by_user_id: number;
      changed_by_name: string;
      reason: string;
      customer_notified: boolean;
      notification_sent: boolean;
      created_at: string;
    }>;
    total_changes: number;
  }> => {
    const response = await api.get(`/appointments/${id}/history`);
    return response.data;
  }
};
